import { LEGAL, formatAddress } from "@/config/legal";
import { Shield } from "lucide-react";

const H = ({ children }) => (
  <h2 className="mt-10 font-serif text-2xl md:text-3xl text-[#0A1128]">{children}</h2>
);
const P = ({ children }) => (
  <p className="mt-3 text-slate-700 text-[15px] leading-relaxed">{children}</p>
);

export default function Privacy() {
  return (
    <div className="scent-wave min-h-[calc(100vh-4rem)]">
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-16" data-testid="privacy-page">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
            <Shield className="w-5 h-5 text-emerald-700" />
          </div>
          <div className="text-[10px] tracking-[0.28em] uppercase text-emerald-700 font-semibold">Legal</div>
        </div>
        <h1 className="mt-4 font-serif text-5xl md:text-6xl tracking-tight text-[#0A1128]">Privacy Policy</h1>
        <div className="mt-3 text-xs text-slate-500">
          Effective {LEGAL.effectiveDate} · Last updated {LEGAL.lastUpdated}
        </div>

        <P>
          This Privacy Policy explains how <b>{LEGAL.companyLegalName}</b> (&ldquo;<b>{LEGAL.dba}</b>,&rdquo; &ldquo;we&rdquo;)
          collects, uses, and shares information when you use the {LEGAL.brandMark} website and services (the
          &ldquo;Service&rdquo;). By using the Service you agree to this Policy.
        </P>

        <H>1. Information We Collect</H>
        <P>We collect the following categories of information:</P>
        <ul className="mt-2 list-disc pl-6 space-y-1.5 text-[15px] text-slate-700">
          <li><b>Account data</b> — name, email, hashed password, role (guest / host / admin), account creation date.</li>
          <li><b>Property data</b> (hosts only) — property name, address, city, country, description, external booking links, uploaded photos, freshness metrics.</li>
          <li><b>Booking &amp; turnover data</b> (hosts only) — guest name, check-in / check-out dates, cleaner email if provided.</li>
          <li><b>Payment data</b> — payment method type, billing zip, last-4 of card. Full card numbers are handled by Stripe and are never stored on our servers.</li>
          <li><b>Usage data</b> — pages viewed, searches performed, badge downloads, share events, IP address, browser type.</li>
          <li><b>Google Calendar data</b> (only if a host connects their Google account) — OAuth refresh token and the ability to create / update / delete events on the connected calendar. We do not read your existing calendar entries.</li>
        </ul>

        <H>2. How We Use Information</H>
        <ul className="mt-2 list-disc pl-6 space-y-1.5 text-[15px] text-slate-700">
          <li>To provide, operate, and improve the Service;</li>
          <li>To issue, verify, and revoke {LEGAL.brandMark} certifications;</li>
          <li>To process payments and send billing receipts;</li>
          <li>To send transactional email (welcome, certification decisions, turnover reminders);</li>
          <li>To send occasional product updates (you can opt out at any time);</li>
          <li>To detect fraud, enforce our Terms, and comply with legal obligations.</li>
        </ul>

        <H>3. How We Share Information</H>
        <P>We share information only with the following categories of recipients:</P>
        <ul className="mt-2 list-disc pl-6 space-y-1.5 text-[15px] text-slate-700">
          <li><b>Stripe</b> — payment processing.</li>
          <li><b>Resend (via Emergent)</b> — transactional email delivery.</li>
          <li><b>Emergent Object Storage</b> — hosting of host-uploaded photos.</li>
          <li><b>Google</b> — only if a host connects their Google Calendar (calendar event creation only).</li>
          <li><b>Legal &amp; safety</b> — when required by valid legal process or to protect the rights and safety of {LEGAL.brandMark}, our users, or the public.</li>
        </ul>
        <P>We do <b>not</b> sell your personal information.</P>

        <H>4. Cookies &amp; Similar Technologies</H>
        <P>
          We use httpOnly, secure cookies (<code>access_token</code>, <code>refresh_token</code>) to keep you signed in.
          We use minimal first-party analytics stored on our own servers to understand aggregate product usage. We do
          not use third-party advertising cookies.
        </P>

        <H>5. Data Retention</H>
        <P>
          We retain account and property data for as long as your account is active, plus a reasonable period thereafter
          to comply with legal obligations (typically 7 years for financial records). You can request deletion of your
          account and associated personal data at any time (see Section 7).
        </P>

        <H>6. Security</H>
        <P>
          We protect your information with industry-standard measures: TLS/HTTPS in transit, at-rest encryption for
          backups, bcrypt-hashed passwords, httpOnly cookies for sessions, and least-privilege access controls. No
          system is 100% secure — please use a strong unique password and enable multi-factor authentication where
          available.
        </P>

        <H>7. Your Rights</H>
        <P>Depending on where you live you may have the following rights:</P>
        <ul className="mt-2 list-disc pl-6 space-y-1.5 text-[15px] text-slate-700">
          <li><b>Access</b> — request a copy of the personal information we hold about you.</li>
          <li><b>Correction</b> — ask us to correct inaccurate information.</li>
          <li><b>Deletion</b> — ask us to delete your personal information (subject to legal retention obligations).</li>
          <li><b>Portability</b> — request your data in a machine-readable format.</li>
          <li><b>Opt-out of marketing</b> — unsubscribe via the link in every marketing email.</li>
          <li><b>California residents (CCPA)</b> — right to know, delete, and opt out of &ldquo;sale&rdquo; of personal
              information (we do not sell). Non-discrimination for exercising any right.</li>
          <li><b>EEA / UK residents (GDPR)</b> — right to lodge a complaint with your local supervisory authority.</li>
        </ul>
        <P>
          To exercise any right, email{" "}
          <a href={`mailto:${LEGAL.legalEmail}`} className="text-emerald-700 underline">{LEGAL.legalEmail}</a>.
          We respond within 30 days.
        </P>

        <H>8. Children</H>
        <P>
          The Service is not directed to children under 16. We do not knowingly collect personal information from
          children under 16. If you believe we have, please contact us and we will delete it.
        </P>

        <H>9. International Transfers</H>
        <P>
          {LEGAL.dba} is operated from the United States. If you access the Service from outside the U.S., your
          information will be transferred to, stored, and processed in the U.S. Where required by law we use
          appropriate safeguards (e.g., Standard Contractual Clauses) for international transfers.
        </P>

        <H>10. Changes to This Policy</H>
        <P>
          If we make material changes we will notify you by email and post a notice on the Service before the changes
          take effect.
        </P>

        <H>11. Contact</H>
        <P>
          <b>{LEGAL.brandMark}</b> — data controller<br />
          Registered in {LEGAL.address.state}, {LEGAL.address.country}<br />
          Full mailing address available upon written request (for GDPR / CCPA data-controller identification)<br />
          Email: <a href={`mailto:${LEGAL.legalEmail}`} className="text-emerald-700 underline">{LEGAL.legalEmail}</a>
        </P>
      </div>
    </div>
  );
}
